import React from 'react';
import { twMerge } from 'tailwind-merge';

import { useApplicationUsageLimits } from 'application/hooks/useApplicationUsageLimits';
import { useApplicationUsageStatistics } from 'application/hooks/useApplicationUsageStatistics';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { useUsageLimits } from 'organizations/hooks/useUsageLimits';
import { useUsageStatistics } from 'organizations/hooks/useUsageStatistics';

import { NUMBER_FORMAT } from 'services/number';
import {
  formatUtilizationLimit,
  formatUtilizationValue,
  getEffectiveUtilizationLimit,
  getLowerUtilizationLimit,
} from 'organizations/utils/utilizationLimit';

import { Heading } from 'components/Heading/Heading';
import { MeterValue } from 'components/Meter';
import { Text } from 'components/Text/Text';
import { Tooltip } from 'components/Tooltip/Tooltip';
import {
  getUtilizationRatio,
  StackedUtilizationMeter,
  UtilizationMeter,
} from 'organizations/components/UtilizationMeter/UtilizationMeter';

/**
 * Read-only workspace utilization cards shared by the Overview page and
 * the workspace utilization settings view. Each card loads its own data
 * and renders nothing until it is available.
 */

function CardShell({
  title,
  tag,
  children,
}: React.PropsWithChildren<{ title: string; tag?: React.ReactNode }>) {
  return (
    <div className="h-full rounded-xl border border-[hsl(var(--ui-separator))] bg-[hsl(var(--ui-fg))] p-4.5">
      <div className="flex items-start justify-between gap-3">
        <Heading level="4" asChild>
          <h3>{title}</h3>
        </Heading>
        {tag}
      </div>
      {children}
    </div>
  );
}

function NoWorkspaceCapTag() {
  return (
    <Tooltip content="This workspace draws from the shared organization limit. Your effective headroom is what other workspaces leave free.">
      <span className="flex-none rounded-md bg-[hsl(var(--ui-control-bg))] px-2 py-1 text-xs font-medium text-[hsl(var(--ui-text))]">
        No workspace cap
      </span>
    </Tooltip>
  );
}

type ReadonlyLimitCardProps = {
  title: string;
  /** Usage of the current workspace. */
  usage: number;
  /** Usage across the whole organization (for the shared-pool view). */
  organizationUsage?: number;
  /** Limit set for this workspace by an organization admin. */
  workspaceLimit?: number | null;
  /** Effective organization-wide limit (org override if set, else plan). */
  organizationLimit?: number | null;
  unit?: 'filesize';
  limitUnit?: string;
  /** Muted suffix after the value in the shared-pool view. */
  valueSuffix?: string;
};

function ReadonlyLimitCard({
  title,
  usage,
  organizationUsage,
  workspaceLimit,
  organizationLimit,
  unit,
  limitUnit,
  valueSuffix = 'in this workspace',
}: ReadonlyLimitCardProps) {
  const hasWorkspaceLimit = Number.isFinite(workspaceLimit);
  const hasOrganizationLimit = Number.isFinite(organizationLimit);

  // Uncapped workspace sharing a finite org pool: show this workspace next
  // to the other workspaces' usage against the org limit.
  if (
    !hasWorkspaceLimit &&
    hasOrganizationLimit &&
    organizationUsage !== undefined
  ) {
    return (
      <SharedPoolCard
        title={title}
        usage={usage}
        organizationUsage={organizationUsage}
        organizationLimit={Number(organizationLimit)}
        unit={unit}
        limitUnit={limitUnit}
        valueSuffix={valueSuffix}
      />
    );
  }

  // The lower of the workspace cap and the org limit — they can drift when
  // the org limit is lowered below an existing workspace cap.
  const effectiveLimit = getLowerUtilizationLimit(
    workspaceLimit,
    organizationLimit
  );
  const hasLimit = Number.isFinite(effectiveLimit);
  const ratio = getUtilizationRatio(usage, effectiveLimit);
  const percent = ratio !== null ? Math.round(ratio * 100) : null;

  // A limit of 0 yields no ratio; usage then always sits at the limit.
  const isAtLimit =
    hasLimit && (ratio !== null ? ratio >= 1 : usage >= Number(effectiveLimit));

  let status: React.ReactNode;
  let statusClasses = '';

  if (!hasLimit) {
    status = 'Unlimited';
  } else if (isAtLimit) {
    status = 'At workspace limit — contact your admin to raise it';
    statusClasses = 'font-semibold text-[hsl(var(--danger-11))]';
  } else if (ratio !== null && ratio >= 0.95) {
    status = `${percent}% — approaching workspace limit`;
    statusClasses = 'font-semibold text-[hsl(var(--danger-11))]';
  } else if (ratio !== null && ratio >= 0.7) {
    status = `${percent}% used`;
    statusClasses = 'font-semibold text-[hsl(var(--warning-11))]';
  } else {
    status = `${percent ?? 0}% used`;
  }

  return (
    <CardShell title={title} tag={hasWorkspaceLimit ? undefined : <NoWorkspaceCapTag />}>
      <div className="mb-3 mt-2.5">
        <MeterValue value={usage} max={effectiveLimit} unit={unit ?? limitUnit} />
      </div>

      <UtilizationMeter
        value={usage}
        max={effectiveLimit}
        tone="status"
        size="medium"
      />

      <Text size="small" className={twMerge('my-0 mt-2', statusClasses)}>
        {status}
      </Text>
    </CardShell>
  );
}

/**
 * Card for an uncapped workspace drawing from a finite organization limit:
 * stacked bar of this workspace vs. other workspaces, with the remaining
 * headroom of the shared pool.
 */
function SharedPoolCard({
  title,
  usage,
  organizationUsage,
  organizationLimit,
  unit,
  limitUnit,
  valueSuffix,
}: {
  title: string;
  usage: number;
  organizationUsage: number;
  organizationLimit: number;
  unit?: 'filesize';
  limitUnit?: string;
  valueSuffix: string;
}) {
  const otherUsage = Math.max(organizationUsage - usage, 0);
  const remaining = Math.max(organizationLimit - organizationUsage, 0);
  const organizationLimitLabel = formatUtilizationLimit(
    organizationLimit,
    unit,
    limitUnit
  );

  return (
    <CardShell title={title} tag={<NoWorkspaceCapTag />}>
      <div className="mb-4 mt-2.5 flex items-baseline gap-1.5">
        <span className="text-lg font-medium leading-none text-[hsl(var(--ui-text-highContrast))]">
          {formatUtilizationValue(usage, unit)}
        </span>
        <Text size="small" inline className="my-0">
          {valueSuffix}
        </Text>
      </div>

      <StackedUtilizationMeter
        value={usage}
        otherValue={otherUsage}
        max={organizationLimit}
        size="medium"
      />

      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs">
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5 text-[hsl(var(--ui-text))]">
            <span className="size-2 flex-none rounded-xs bg-[hsl(var(--primary-9))]" />
            This workspace · {formatUtilizationValue(usage, unit)}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[hsl(var(--ui-text))]">
            <span className="size-2 flex-none rounded-xs bg-[hsl(var(--ui-8))]" />
            Other workspaces · {formatUtilizationValue(otherUsage, unit)}
          </span>
        </div>

        <span className="font-medium text-[hsl(var(--ui-text-placeholder))]">
          Org limit {organizationLimitLabel} ·{' '}
          <span className="text-[hsl(var(--success-11))]">
            {formatUtilizationValue(remaining, unit)} remaining
          </span>
        </span>
      </div>
    </CardShell>
  );
}

/**
 * Live streams utilization of the current workspace. Follows the plan's
 * running vs. monthly-active stream limit mode.
 */
export function WorkspaceStreamsUtilizationCard() {
  const { data: currentPlan } = useCurrentPlan();
  const { data: organizationUsageStatistics } = useUsageStatistics();
  const {
    monthlyActiveLiveStreams,
    runningStreams,
    isLoading: isLoadingLimits,
  } = useApplicationUsageLimits();
  const {
    monthlyActiveLiveStreams: organizationMonthlyActiveLiveStreams,
    runningStreams: organizationRunningStreams,
    isLoading: isLoadingOrganizationLimits,
  } = useUsageLimits();

  if (
    currentPlan === undefined ||
    isLoadingLimits ||
    isLoadingOrganizationLimits
  ) {
    return null;
  }

  const useRunningStreamLimit = Boolean(currentPlan?.use_running_stream_limit);
  const streamLimit = useRunningStreamLimit
    ? runningStreams
    : monthlyActiveLiveStreams;
  const organizationStreamLimit = useRunningStreamLimit
    ? organizationRunningStreams
    : organizationMonthlyActiveLiveStreams;
  const organizationUsage = useRunningStreamLimit
    ? organizationUsageStatistics?.running_stream_count
    : organizationUsageStatistics?.realtime_stream_count;

  return (
    <ReadonlyLimitCard
      title={
        useRunningStreamLimit
          ? 'Running live streams'
          : 'Monthly active live streams'
      }
      usage={streamLimit.usage ?? 0}
      organizationUsage={organizationUsage}
      workspaceLimit={streamLimit.userLimit}
      organizationLimit={getEffectiveUtilizationLimit(
        organizationStreamLimit.planLimit,
        organizationStreamLimit.userLimit
      )}
      valueSuffix={
        useRunningStreamLimit ? 'running in this workspace' : 'in this workspace'
      }
    />
  );
}

/** Recorded analytics (file processing minutes) of the current workspace. */
export function WorkspaceRecordedAnalyticsUtilizationCard() {
  const { data: usageStatistics } = useApplicationUsageStatistics();
  const { data: organizationUsageStatistics } = useUsageStatistics();
  const { fileProcessingMinutes, isLoading: isLoadingLimits } =
    useApplicationUsageLimits();
  const {
    fileProcessingMinutes: organizationFileProcessingMinutes,
    isLoading: isLoadingOrganizationLimits,
  } = useUsageLimits();

  if (!usageStatistics || isLoadingLimits || isLoadingOrganizationLimits) {
    return null;
  }

  return (
    <ReadonlyLimitCard
      title="Recorded analytics"
      usage={usageStatistics.file_processing_minutes}
      organizationUsage={organizationUsageStatistics?.file_processing_minutes}
      workspaceLimit={fileProcessingMinutes.userLimit}
      organizationLimit={getEffectiveUtilizationLimit(
        organizationFileProcessingMinutes.planLimit,
        organizationFileProcessingMinutes.userLimit
      )}
      limitUnit="minutes"
    />
  );
}

/** Cloud storage utilization of the current workspace. */
export function WorkspaceCloudStorageUtilizationCard() {
  const { data: usageStatistics } = useApplicationUsageStatistics();
  const { data: organizationUsageStatistics } = useUsageStatistics();
  const { cloudStorageBytes, isLoading: isLoadingLimits } =
    useApplicationUsageLimits();
  const {
    cloudStorageBytes: organizationCloudStorageBytes,
    isLoading: isLoadingOrganizationLimits,
  } = useUsageLimits();

  if (!usageStatistics || isLoadingLimits || isLoadingOrganizationLimits) {
    return null;
  }

  return (
    <ReadonlyLimitCard
      title="Cloud storage"
      usage={usageStatistics.cloud_storage_bytes}
      organizationUsage={organizationUsageStatistics?.cloud_storage_bytes}
      workspaceLimit={cloudStorageBytes.userLimit}
      organizationLimit={getEffectiveUtilizationLimit(
        organizationCloudStorageBytes.planLimit,
        organizationCloudStorageBytes.userLimit
      )}
      unit="filesize"
    />
  );
}

/**
 * Effective recording retention of the current workspace. Renders nothing
 * when the plan cannot retain recordings.
 */
export function WorkspaceRetentionUtilizationCard() {
  const { data: currentPlan } = useCurrentPlan();
  const { recordingRetentionDays, isLoading: isLoadingLimits } =
    useApplicationUsageLimits();
  const {
    recordingRetentionDays: organizationRecordingRetentionDays,
    isLoading: isLoadingOrganizationLimits,
  } = useUsageLimits();

  if (
    !currentPlan?.can_retain_recordings ||
    isLoadingLimits ||
    isLoadingOrganizationLimits
  ) {
    return null;
  }

  const organizationLimit = getEffectiveUtilizationLimit(
    organizationRecordingRetentionDays.planLimit,
    organizationRecordingRetentionDays.userLimit
  );
  // Lower of the workspace retention and the org retention max.
  const effectiveRetention = getLowerUtilizationLimit(
    recordingRetentionDays.userLimit,
    organizationLimit
  );
  const hasWorkspaceLimit = Number.isFinite(recordingRetentionDays.userLimit);

  return (
    <CardShell title="Retention">
      <div className="mb-1 mt-2.5 text-lg font-medium leading-none text-[hsl(var(--ui-text-highContrast))]">
        {effectiveRetention !== null
          ? `${NUMBER_FORMAT.format(effectiveRetention)} days`
          : 'Unlimited'}
      </div>

      <Text size="small" className="my-0 mt-2">
        {hasWorkspaceLimit
          ? `Set by admin${
              organizationLimit !== null
                ? ` · org max is ${NUMBER_FORMAT.format(
                    organizationLimit
                  )} days`
                : ''
            }`
          : 'Inherited from the organization'}
      </Text>
    </CardShell>
  );
}

/** Deployment count of the current workspace; shares the org-wide limit. */
export function WorkspaceDeploymentsUtilizationCard() {
  const { data: currentPlan } = useCurrentPlan();
  const { data: usageStatistics } = useApplicationUsageStatistics();
  const { data: organizationUsageStatistics } = useUsageStatistics();

  if (currentPlan === undefined || !usageStatistics) {
    return null;
  }

  const organizationLimit = Number.isFinite(
    currentPlan?.running_deployment_limit
  )
    ? Number(currentPlan?.running_deployment_limit)
    : null;

  return (
    <ReadonlyLimitCard
      title="Running workflow deployments"
      usage={usageStatistics.deployment_count}
      organizationUsage={organizationUsageStatistics?.deployment_count}
      workspaceLimit={null}
      organizationLimit={organizationLimit}
      valueSuffix="running in this workspace"
    />
  );
}
